import {WorkspaceReservationCountPerNode, WorkspaceReservationCountPerNodeByDate} from '../../data/entities';
import {isNodeClosed} from '../../data/openingDays/helpers';
import {getChildren, getNode} from '../../util/buildingNodes/treeHelpers';
import {formatDateAsKey} from '@lib/utils';
import {GetNodeInformationArgs, NodeInformation} from './types';
import {
  getCountForMembersReservationOfWorkspaceReservation,
  getWorkspaceReservationSpaceNodeId,
} from '../../data/workspaceReservation';

const checkIfNodeHasInsufficientSpaceForGroupBooking = (
  numberOfInvitees: number,
  occupation: number,
  capacity: number,
) => {
  if (!numberOfInvitees) {
    return false;
  }
  return occupation + numberOfInvitees + 1 > capacity;
};
const getWorkspaceReservationCountPerNode = (
  nodeId: string,
  workspaceReservationsOnDate: WorkspaceReservationCountPerNode,
): number => workspaceReservationsOnDate[nodeId] || 0;

const getWorkspaceReservationCountPerDate = (
  date: Date,
  workspaceReservationCountPerNodeByDate: WorkspaceReservationCountPerNodeByDate,
): WorkspaceReservationCountPerNode => {
  const formattedDate = formatDateAsKey(date);
  return workspaceReservationCountPerNodeByDate[formattedDate];
};

export const nodeNotFoundResult = {
  occupation: 0,
  capacity: 0,
  isFull: false,
  isClosed: false,
  hasInsufficientSpaceForGroupBooking: false,
};

export const getNodeInformation = ({
  nodeId,
  date,
  buildings,
  floors,
  areas,
  workspaceReservationCountPerNodeByDate,
  openingDaysExceptions,
  numberOfInvitees = 0,
  workspaceReservation,
  childInfos = {},
}: GetNodeInformationArgs): NodeInformation => {
  if (!nodeId || !date || !workspaceReservationCountPerNodeByDate) {
    return nodeNotFoundResult;
  }

  const node = getNode(nodeId, buildings, floors, areas);
  if (!node) {
    return nodeNotFoundResult;
  }

  const {capacity} = node;
  // If the node or all of its children is closed, we don't care about the rest
  // REMARK: We MUST consider closed nodes as full, only desk bookable, and insufficient space for group booking so parent nodes can be checked for being full correctly
  if (isNodeClosed({nodeId, date, buildings, floors, areas, openingDaysExceptions})) {
    return {
      occupation: capacity,
      capacity: capacity,
      isFull: true,
      isClosed: true,
      hasInsufficientSpaceForGroupBooking: true,
    };
  }

  const workspaceReservationsOnDate =
    getWorkspaceReservationCountPerDate(date, workspaceReservationCountPerNodeByDate) || {};
  const children = getChildren(node);

  if (!children.length) {
    const currentMembersReservationsOfWorkspaceReservationCount =
      getCountForMembersReservationOfWorkspaceReservation(workspaceReservation);
    const occupationPerNodeCount = getWorkspaceReservationCountPerNode(nodeId, workspaceReservationsOnDate);
    const occupationWithoutCurrentReservationMembers =
      occupationPerNodeCount - currentMembersReservationsOfWorkspaceReservationCount;
    const currentMember = workspaceReservation?.currentMember;
    const currentMemberReservationSpaceNodeId =
      currentMember &&
      getWorkspaceReservationSpaceNodeId({
        ...currentMember.location,
        nodeId: currentMember.nodeId,
      });
    const isWorkspaceReservationOnThisNode = currentMemberReservationSpaceNodeId === nodeId;

    const hasInsufficientSpaceForGroupBooking = checkIfNodeHasInsufficientSpaceForGroupBooking(
      numberOfInvitees,
      isWorkspaceReservationOnThisNode ? occupationWithoutCurrentReservationMembers : occupationPerNodeCount,
      capacity,
    );

    return {
      occupation: isWorkspaceReservationOnThisNode
        ? occupationWithoutCurrentReservationMembers
        : occupationPerNodeCount,
      capacity: capacity,
      isFull: isWorkspaceReservationOnThisNode
        ? occupationWithoutCurrentReservationMembers >= capacity
        : occupationPerNodeCount >= capacity,
      isClosed: false,
      hasInsufficientSpaceForGroupBooking,
    };
  }

  const {childrenOccupation, childrenHasInsufficientSpaceForGroupBooking} = children.reduce(
    (acc, childNodeId) => {
      const {
        occupation: childOccupation,
        hasInsufficientSpaceForGroupBooking: childHasInsufficientSpaceForGroupBooking,
      } =
        childInfos[childNodeId] ??
        getNodeInformation({
          nodeId: childNodeId,
          date,
          buildings,
          floors,
          areas,
          workspaceReservationCountPerNodeByDate,
          openingDaysExceptions,
          numberOfInvitees,
          workspaceReservation,
        });

      return {
        childrenOccupation: (acc.childrenOccupation += childOccupation),
        childrenHasInsufficientSpaceForGroupBooking:
          acc.childrenHasInsufficientSpaceForGroupBooking && childHasInsufficientSpaceForGroupBooking,
      };
    },
    {
      childrenOccupation: 0,
      childrenHasInsufficientSpaceForGroupBooking: true,
    },
  );

  return {
    occupation: childrenOccupation,
    capacity: capacity,
    isFull: childrenOccupation >= capacity,
    isClosed: false,
    hasInsufficientSpaceForGroupBooking: childrenHasInsufficientSpaceForGroupBooking,
  };
};
